import { useEffect,useRef, useState } from "react";
import { Check, ChevronDown, Loader2,X } from "lucide-react";

import style from "./SelectHospital.module.css";

// `placeholder` and `emptyLabel` are additive: without them the referral wording is
// exactly what it was. The eLab flow reuses this select for a laboratory, which is
// neither a clinic nor a referral type.
export const SelectHospital = ({
  options,
  value,
  onChange,
  fullWidth = false,
  isReferral = false,
  isLoading = false,
  placeholder,
  emptyLabel,
}) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const comboboxRef = useRef(null);

  const selectedHospital = options.find((option) => option.value === value);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredHospitals = options
    .filter((hospital) =>
      hospital.label.toLowerCase().includes(normalizedSearch)
    )
    .sort((a, b) => {
      const aLabel = a.label.toLowerCase();
      const bLabel = b.label.toLowerCase();
      const aStarts = normalizedSearch && aLabel.startsWith(normalizedSearch);
      const bStarts = normalizedSearch && bLabel.startsWith(normalizedSearch);

      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return 0;
    });

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setIsOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [comboboxRef]);

  const renderDropdownContent = () => {
    if (isLoading) {
      return (
        <div className={style.dropdownItem}>
          <div className={style.loadingContainer}>
            <Loader2 className={style.spinner} size={20} />
            <span className={style.loadingText}>Loading...</span>
          </div>
        </div>
      );
    }

    if (!filteredHospitals?.length) {
      return (
        <div className={style.dropdownItem}>
          <div className={style.noResults}>
            {emptyLabel
              ? `${emptyLabel} "${search}"`
              : isReferral
              ? `No referral types found for "${search}"`
              : `No clinics found for "${search}"`}
          </div>
          <div className={style.noResultsHelper}>
            {isReferral
              ? "Try searching with a different term"
              : "Try searching with a different term"}
          </div>
        </div>
      );
    }

    return filteredHospitals.map((hospital) => (
      <div
        key={hospital.value}
        className={style.dropdownItem}
        onClick={() => {
          onChange(hospital.value);
          setIsOpen(false);
          setSearch(hospital.label);
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {value === hospital.value && (
            <div className={style.selectedOption}>
              <Check
                style={{
                  color: "var(--Gray-950)",
                  width: "16px",
                  height: "16px",
                }}
              />
            </div>
          )}
          <div className={style.hospitalName}>{hospital.label}</div>
        </div>
      </div>
    ));
  };

  useEffect(() => {
    if (selectedHospital) {
      setSearch(selectedHospital.label);
    } else {
      setSearch("");
    }
  }, [selectedHospital]);

  return (
    <div
      className={`${style.container} ${fullWidth ? style.fullWidth : ""}`}
      ref={comboboxRef}
    >
      <div className={style.comboboxContainer}>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            style={{ width: "100%", paddingRight: "70px" }}
            placeholder={
              placeholder ??
              (isReferral ? "Search referral type" : "Search clinic")
            }
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            className="form-control"
            value={search}
          />
          {value && (
            <button
              style={{
                position: "absolute",
                right: "40px",
                top: "50%",
                transform: "translateY(-54%)",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <X
                style={{
                  color: "var(--Gray-500)",
                  width: "16px",
                  height: "16px",
                }}
                onClick={() => {
                  onChange("");
                  setIsOpen(false);
                  setSearch("");
                }}
              />
            </button>
          )}
          <ChevronDown
            style={{
              color: "var(--Gray-500)",
              width: "16px",
              height: "16px",
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
        </div>
        {isOpen && (
          <div className={style.dropdown}>{renderDropdownContent()}</div>
        )}
      </div>
    </div>
  );
};
